// Settings menu data: which SettingGroup tabs exist, their module flags and icons.
// The menu groups them as Account / Organisation / Products / Integrations / Developer.

import { CONSTANTS_SETTINGS } from './settingsContainer';

// ── SettingGroup IDs from DB ────────────────────────────────────────────────
// Keep in sync with Organisation.SettingGroup. Values confirmed 2026-04-21.
export const SETTING_GROUP_IDS = {
	ZEZAMII_LOCKER: 23,
	ACCESS: 24,
	GENERAL: 25,
	CONTROL: 26,
	INNER_RANGE: 27,
	AVIGILON_ALTA: 32,
	TTLOCK: 33,
	ROOMS: 34, // confirmed against dev + prod DB 2026-06-06
	VERKADA: 35,
	// 36 = Access Control (exists on dev, reserved on prod)
	VORTEX: 37,
	INCEPTION: 38, // seeded by App Server migrations/add-inception-integration.sql
	MICROSOFT_365: 42, // seeded by App Server migrations/add-m365-integration.sql
	SHELLY: 44 // 'Shelly Presence', seeded by App Server migrations/add-shelly-presence.sql
};

// ── Static menu keys (not backed by SettingGroup) ───────────────────────────
export const STATIC_KEYS = {
	PROFILE: CONSTANTS_SETTINGS.PERSON_SETTING_TYPE,
	PRODUCTS: 'products',
	INTEGRATIONS: 'integrations',
	BILLING: 'billing',
	DEVICE_CATEGORIES: 'device-categories',
	PARTNER_ACCESS: 'partner-access'
};

// Org-level setting tabs: General (always) and product tabs gated by their flag. No `label` on
// purpose: a tab's name is its DB title. The menu shows them under Organisation and Products.
export const ORG_SETTING_TABS = [
	{ key: SETTING_GROUP_IDS.GENERAL /* no flag */ },
	{ key: SETTING_GROUP_IDS.ACCESS, moduleFlag: CONSTANTS_SETTINGS.ACCESS_MODULE },
	{ key: SETTING_GROUP_IDS.ZEZAMII_LOCKER, moduleFlag: CONSTANTS_SETTINGS.LOCKER_MODULE }
];

// Integration setting tabs, listed under the menu's Integrations group.
export const INTEGRATION_TABS = [
	{ key: SETTING_GROUP_IDS.CONTROL, moduleFlag: CONSTANTS_SETTINGS.CONTROL_MODULE },
	{ key: SETTING_GROUP_IDS.INNER_RANGE, moduleFlag: CONSTANTS_SETTINGS.INNER_RANGE_MODULE },
	{ key: SETTING_GROUP_IDS.TTLOCK, moduleFlag: CONSTANTS_SETTINGS.TTLOCK_MODULE },
	{ key: SETTING_GROUP_IDS.VERKADA, moduleFlag: CONSTANTS_SETTINGS.VERKADA_MODULE },
	{ key: SETTING_GROUP_IDS.VORTEX, moduleFlag: CONSTANTS_SETTINGS.VORTEX_MODULE },
	{ key: SETTING_GROUP_IDS.INCEPTION, moduleFlag: CONSTANTS_SETTINGS.INCEPTION_MODULE },
	{
		key: SETTING_GROUP_IDS.MICROSOFT_365,
		moduleFlag: CONSTANTS_SETTINGS.M365_MODULE,
		// Gated identically to its Integrations card — hidden unless the org is entitled, so a
		// module-toggle-ON-but-flag-OFF (revoked/seeded) org can't reach the tab or its Verify button.
		featureFlag: 'm365Bookings'
	},
	// Shelly Presence ships dark: gated by `presence` exactly like its Integrations card.
	{ key: SETTING_GROUP_IDS.SHELLY, moduleFlag: CONSTANTS_SETTINGS.SHELLY_MODULE, featureFlag: 'presence' }
];

// ZezamiiIcon name per settings tab (static keys and SettingGroup IDs).
export const SETTINGS_TAB_ICONS = {
	[STATIC_KEYS.PROFILE]: 'user',
	[STATIC_KEYS.BILLING]: 'card',
	[STATIC_KEYS.INTEGRATIONS]: 'plug',
	[SETTING_GROUP_IDS.GENERAL]: 'settings',
	[SETTING_GROUP_IDS.ACCESS]: 'product-access',
	[SETTING_GROUP_IDS.ZEZAMII_LOCKER]: 'product-lockers',
	[SETTING_GROUP_IDS.CONTROL]: 'digital-lock',
	[SETTING_GROUP_IDS.TTLOCK]: 'lock',
	[SETTING_GROUP_IDS.INNER_RANGE]: 'access-panel',
	[SETTING_GROUP_IDS.INCEPTION]: 'access-panel',
	[SETTING_GROUP_IDS.VERKADA]: 'dome-camera',
	[SETTING_GROUP_IDS.VORTEX]: 'bullet-camera',
	[SETTING_GROUP_IDS.MICROSOFT_365]: 'calendar',
	[SETTING_GROUP_IDS.SHELLY]: 'sensor'
};

// ── Top-level settings navigation ───────────────────────────────────────────
// Four sections: General (account, organisation, integrations, developer), Products & licences (coverage plus each
// product's own settings tab), Billing, Partner access. Menu KEYS are unchanged, so every existing
// setSelectedSettingGroup(...) caller and stored selection still lands in the same place.
export const SETTINGS_NAV_SECTIONS = Object.freeze({
	GENERAL: 'General',
	PRODUCTS: 'Products & licences',
	BILLING: 'Billing',
	PARTNER_ACCESS: 'Partner access'
});

// Deep-link aliases for `/…/settings?section=<alias>`: old and new names resolve to the existing menu keys. A
// numeric value is a SettingGroup ID (a product or integration tab). Gates still apply after resolution: a
// section the person cannot see falls back like any other invalid selection.
export const SETTINGS_SECTION_ALIASES = Object.freeze({
	'general': SETTING_GROUP_IDS.GENERAL,
	'organisation': SETTING_GROUP_IDS.GENERAL,
	'organization': SETTING_GROUP_IDS.GENERAL,
	'account': STATIC_KEYS.PROFILE,
	'profile': STATIC_KEYS.PROFILE,
	'products': STATIC_KEYS.PRODUCTS,
	'all-products': STATIC_KEYS.PRODUCTS,
	'licences': STATIC_KEYS.PRODUCTS,
	'licenses': STATIC_KEYS.PRODUCTS,
	'licensing': STATIC_KEYS.PRODUCTS,
	'products-licences': STATIC_KEYS.PRODUCTS,
	'integrations': STATIC_KEYS.INTEGRATIONS,
	'billing': STATIC_KEYS.BILLING,
	'invoices': STATIC_KEYS.BILLING,
	'partner-access': STATIC_KEYS.PARTNER_ACCESS,
	'partner': STATIC_KEYS.PARTNER_ACCESS,
	'device-categories': STATIC_KEYS.DEVICE_CATEGORIES
});

/** `?section=` value → menu key, or null when it names nothing. */
export const resolveSettingsSection = (raw) => {
	if (raw == null) return null;
	const value = String(raw).trim().toLowerCase();
	if (!value) return null;
	if (/^\d+$/.test(value)) {
		const id = Number(value);
		return Object.values(SETTING_GROUP_IDS).includes(id) ? id : null;
	}
	return Object.prototype.hasOwnProperty.call(SETTINGS_SECTION_ALIASES, value)
		? SETTINGS_SECTION_ALIASES[value]
		: null;
};
